import React, { FC, useContext, useMemo, useState } from 'react';

import { Form, Formik } from 'formik';

import BasicModal from '../../../../../../components/modals/basicModal';
import styled from 'styled-components';

import aampeTheme from '../../../../../../constants/aampeTheme';
import { ModalHeaderStyle } from '../../../../../attributes/constants';
import { getValidationSchema, ModalDescription } from './constants';

import { EditorContext } from '../../../../../../context/EditorContext';

import { sanitizeObject } from '../../../../../../helpers/sanitize.helper';
import {
    getButtonPayload,
    getInitialValues,
    showButtonSuccessMessage,
} from './helpers';

import useFormulaButtonsUpdateOrCreate from '../../../../../../hooks/formula-buttons/useFormulaButtonsUpdateOrCreate';

import {
    NotificationButtonElement,
    NotificationButtonFormValues,
} from '../../../../../../models/formula.model';

import NotificationActionButton from './NotificationActionButton';
import NotificationButtonForm from './NotificationButtonForm';

interface NotificationButtonModalProps {
    handleClose?: () => void;
    data: NotificationButtonElement[] | undefined;
}

const NotificationButtonModal: FC<NotificationButtonModalProps> = ({
    handleClose,
    data,
}) => {
    const {
        editorDetails: { formula },
    } = useContext(EditorContext);
    const [currentIndex, setCurrentIndex] = useState<number>(0);

    const updateButtonFormula = useFormulaButtonsUpdateOrCreate(
        Number(formula?.id!),
    );

    const handleSubmit = (values: NotificationButtonFormValues) => {
        const sanitizedData = sanitizeObject(values);

        const buttonPayload: NotificationButtonElement[] =
            getButtonPayload(sanitizedData);

        updateButtonFormula.mutate(
            { buttonPayload },
            {
                onSuccess() {
                    showButtonSuccessMessage(
                        buttonPayload.length,
                        sanitizedData,
                    );
                    handleClose?.();
                },
            },
        );
    };

    const initialValues = useMemo<NotificationButtonFormValues>(
        () => getInitialValues(data, formula?.ios_notification_category!),
        [data, formula],
    );

    const isUpdating = useMemo<boolean>(() => !!data?.length, [data]);

    return (
        <BasicModal
            width="512px"
            childContainerStyle={{ padding: aampeTheme.spacing4(4, 8, 8, 8) }}
            description={ModalDescription}
            header={`${isUpdating ? 'Update' : 'Add'} action buttons`}
            handleClose={handleClose}
            headerStyle={ModalHeaderStyle}
        >
            <Formik
                onSubmit={() => {}}
                initialValues={initialValues}
                validationSchema={getValidationSchema}
                validateOnMount
                enableReinitialize
            >
                {() => (
                    <StyledForm>
                        <NotificationButtonForm
                            setCurrentIndex={setCurrentIndex}
                            currentIndex={currentIndex}
                        />
                        <NotificationActionButton
                            setCurrentIndex={setCurrentIndex}
                            currentIndex={currentIndex}
                            handleClose={handleClose}
                            handleSubmit={handleSubmit}
                            isUpdating={isUpdating}
                        />
                    </StyledForm>
                )}
            </Formik>
        </BasicModal>
    );
};

const StyledForm = styled(Form)({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(4),
});

export default NotificationButtonModal;
